import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CampaignIcon from "@mui/icons-material/Campaign";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";

import { StyledCard } from "../common/StyledCard";
import { AnnouncementSummaryDto } from "../../shared/dtos/announcement/AnnouncementSummaryDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface AnnouncementCardProps {
  announcement: AnnouncementSummaryDto;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  announcement,
  onView,
  onEdit,
  onDelete,
}) => {
  const isExpired = announcement.expiryDate
    ? new Date(announcement.expiryDate) < new Date()
    : false;

  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box sx={{ maxWidth: "70%" }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {announcement.title}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip
                label={announcement.audience.replace(/_/g, " ")}
                size="small"
                color="secondary"
                variant="outlined"
                icon={<CampaignIcon fontSize="small" />}
                sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
              />
              {isExpired && (
                <Chip
                  label="Expired"
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row">
            {onView && (
              <Tooltip title="Read Announcement">
                <IconButton size="small" color="info" onClick={onView}>
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Content Preview */}
        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {announcement.content}
          </Typography>
        </Box>

        {/* Footer Details */}
        <Stack spacing={0.8} sx={{ mt: "auto" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <PersonIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              By {announcement.authorName || "Staff"}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <AccessTimeIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Pub: {formatDateForDisplay(announcement.publishDate)}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default AnnouncementCard;
